<div class="settings-page">

  <div class="page-header">
    <div class="page-title">
      <svg class="page-title__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M11.828 2.25c-.916 0-1.699.663-1.85 1.567l-.091.549a.798.798 0 01-.517.608 7.45 7.45 0 00-.478.198.798.798 0 01-.796-.064l-.453-.324a1.875 1.875 0 00-2.416.2l-.243.243a1.875 1.875 0 00-.2 2.416l.324.453a.798.798 0 01.064.796 7.448 7.448 0 00-.198.478.798.798 0 01-.608.517l-.549.091A1.875 1.875 0 002.25 11.828v.344c0 .916.663 1.699 1.567 1.85l.549.091c.281.047.508.25.608.517.06.162.127.321.198.478a.798.798 0 01-.064.796l-.324.453a1.875 1.875 0 00.2 2.416l.243.243c.648.648 1.67.733 2.416.2l.453-.324a.798.798 0 01.796-.064c.157.071.316.137.478.198.267.1.47.327.517.608l.091.549a1.875 1.875 0 001.85 1.567h.344c.916 0 1.699-.663 1.85-1.567l.091-.549a.798.798 0 01.517-.608 7.52 7.52 0 00.478-.198.798.798 0 01.796.064l.453.324a1.875 1.875 0 002.416-.2l.243-.243c.648-.648.733-1.67.2-2.416l-.324-.453a.798.798 0 01-.064-.796c.071-.157.137-.316.198-.478.1-.267.327-.47.608-.517l.549-.091A1.875 1.875 0 0021.75 12.172v-.344c0-.916-.663-1.699-1.567-1.85l-.549-.091a.798.798 0 01-.608-.517 7.507 7.507 0 00-.198-.478.798.798 0 01.064-.796l.324-.453a1.875 1.875 0 00-.2-2.416l-.243-.243a1.875 1.875 0 00-2.416-.2l-.453.324a.798.798 0 01-.796.064 7.462 7.462 0 00-.478-.198.798.798 0 01-.517-.608l-.091-.549a1.875 1.875 0 00-1.85-1.567h-.344zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" fill="currentColor"/>
      </svg>
      <h1 class="page-title__text">Settings</h1>
    </div>
    <p class="page-title__sub">Manage your account, security, and preferences.</p>
  </div>

  <div class="settings-stack">

    <!-- Profile -->
    <div class="settings-card">
      <div class="settings-card__header">
        <p class="settings-card__title">Profile</p>
        <p class="settings-card__sub">Your personal information.</p>
      </div>
      <div class="settings-card__body" [formGroup]="profileForm">
        @if (user$ | async; as user) {
          <div class="avatar-row">
            <div class="sidebar__avatar">{{ initials(user.name) }}</div>
            <div class="avatar-row__info">
              <span class="avatar-row__name">{{ user.name }}</span>
              <span class="avatar-row__email">{{ user.email }}</span>
            </div>
            <button class="btn--secondary" (click)="openAvatarDialog()">Change avatar</button>
          </div>
        }
        <div class="settings-fields">
          <div class="form-field">
            <label class="form-field__label">Full name</label>
            <input type="text" class="field-input field-input--text" formControlName="name" />
          </div>
          <div class="form-field">
            <label class="form-field__label">Email address</label>
            <input type="email" class="field-input field-input--text field-input--readonly" formControlName="email" />
          </div>
        </div>
        <div>
          <button class="btn--primary" [disabled]="isSavingProfile" (click)="saveProfile()">{{ isSavingProfile ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- Security -->
    <div class="settings-card">
      <div class="settings-card__header">
        <p class="settings-card__title">Security</p>
        <p class="settings-card__sub">Manage your password and two-factor authentication.</p>
      </div>
      <div class="settings-row">
        <div class="settings-row__left">
          <span class="settings-row__label">Password</span>
          <span class="settings-row__sub">Last changed 3 months ago</span>
        </div>
        <div class="settings-row__right">
          <button class="btn--secondary" (click)="openPasswordDialog()">Change password</button>
        </div>
      </div>
      <div class="settings-row">
        <div class="settings-row__left">
          <span class="settings-row__label">Two-factor authentication</span>
          @if (isLoadingTwoFactorStatus) {
            <span class="settings-row__sub">Loading…</span>
          } @else if (twoFactorStatus?.enabled) {
            <span class="settings-row__sub">{{ twoFactorStatusLabel }}</span>
          } @else {
            <span class="settings-row__sub">Add an extra layer of security</span>
          }
        </div>
        <div class="settings-row__right">
          @if (!isLoadingTwoFactorStatus) {
            @if (twoFactorStatus?.enabled) {
              <span class="status-chip status-chip--success"><span class="status-chip__dot"></span>Enabled</span>
              <button class="link-danger" (click)="openDisableDialog()">Disable</button>
            } @else {
              <span class="status-chip status-chip--muted"><span class="status-chip__dot"></span>Disabled</span>
              <button class="btn--secondary" (click)="openEnableSetupDialog()">Enable</button>
            }
          }
        </div>
      </div>
    </div>

    <!-- Notifications -->
    <div class="settings-card">
      <div class="settings-card__header">
        <p class="settings-card__title">Notifications</p>
        <p class="settings-card__sub">Choose what updates you receive by email.</p>
      </div>
      @if (isLoadingNotifications) {
        <div class="settings-row settings-row--no-border">
          <span class="settings-row__sub">Loading…</span>
        </div>
      } @else {
        <form [formGroup]="notificationsForm">
          <div class="settings-row">
            <div class="settings-row__left">
              <span class="settings-row__label">Low balance alerts</span>
              <span class="settings-row__sub">Get notified when your wallet balance runs low</span>
            </div>
            <div class="settings-row__right">
              @if (notificationsForm.value.low_balance_alerts) {
                <div class="amount-input-wrap">
                  <span class="amount-input-wrap__prefix">{{ currencySymbol(notificationsCurrency) }}</span>
                  <input type="number" min="0" step="0.01" placeholder="0.00" formControlName="low_balance_threshold" />
                </div>
              }
              <label class="toggle">
                <input type="checkbox" class="toggle__input" formControlName="low_balance_alerts" />
                <span class="toggle__track"><span class="toggle__thumb"></span></span>
              </label>
            </div>
          </div>
          <div class="settings-row">
            <div class="settings-row__left">
              <span class="settings-row__label">Payment receipts</span>
              <span class="settings-row__sub">Get an email receipt for every top-up</span>
            </div>
            <label class="toggle">
              <input type="checkbox" class="toggle__input" formControlName="payment_receipts" />
              <span class="toggle__track"><span class="toggle__thumb"></span></span>
            </label>
          </div>
          <div class="settings-row settings-row--no-border">
            <div class="settings-row__left">
              <span class="settings-row__label">Usage alerts</span>
              <span class="settings-row__sub">Get notified when your API usage crosses a threshold</span>
            </div>
            <div class="settings-row__right">
              @if (notificationsForm.value.usage_alerts) {
                <div class="amount-input-wrap">
                  <span class="amount-input-wrap__prefix">{{ currencySymbol(notificationsCurrency) }}</span>
                  <input type="number" min="0" step="0.01" placeholder="0.00" formControlName="usage_alert_threshold" />
                </div>
              }
              <label class="toggle">
                <input type="checkbox" class="toggle__input" formControlName="usage_alerts" />
                <span class="toggle__track"><span class="toggle__thumb"></span></span>
              </label>
            </div>
          </div>
          <div class="settings-card__footer">
            <button class="btn--primary" [disabled]="isSavingNotifications || !notificationsForm.dirty" (click)="saveNotifications()">
              {{ isSavingNotifications ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </form>
      }
    </div>

    <!-- Danger zone -->
    <div class="settings-card settings-card--danger">
      <div class="settings-row settings-row--no-border">
        <div class="settings-row__left">
          <span class="settings-row__label settings-row__label--bold">Delete account</span>
          <span class="settings-row__sub">Permanently delete your account and all data.</span>
        </div>
        <div class="settings-row__right">
          <button class="btn--danger" (click)="confirmDeleteAccount()">Delete account</button>
        </div>
      </div>
    </div>

  </div>
</div>
